import { User, CreditCard, ShoppingBag, Calendar, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface WalkInSaleConfirmStepProps {
  data: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    paymentType: 'leasing' | 'direct';
    productDescription: string;
    deliveryDate: string;
    deliveryDateKnown: boolean;
    notes: string;
  };
  onConfirm: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export function WalkInSaleConfirmStep({ data, onConfirm, onBack, isSubmitting }: WalkInSaleConfirmStepProps) {
  return (
    <div>
      {/* Customer info card */}
      <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <User className="w-4 h-4 text-blue-400" />
          <span className="text-sm font-medium text-slate-300">Kunde</span>
        </div>
        <div className="space-y-1 text-sm">
          <div className="text-slate-100 font-medium">{data.firstName} {data.lastName}</div>
          <div className="text-slate-400">{data.phone}</div>
          <div className="text-slate-400">{data.email}</div>
        </div>
      </div>

      {/* Sale info card */}
      <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <ShoppingBag className="w-4 h-4 text-green-400" />
          <span className="text-sm font-medium text-slate-300">Verkauf</span>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-400">Zahlungsart</span>
            <span className="text-slate-100 flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5" />
              {data.paymentType === 'leasing' ? 'Leasing (36 Monate)' : 'Direktkauf'}
            </span>
          </div>
          {data.productDescription && (
            <div className="flex justify-between">
              <span className="text-slate-400">Produkt</span>
              <span className="text-slate-100">{data.productDescription}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-400">Liefertermin</span>
            <span className="text-slate-100 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {data.deliveryDateKnown && data.deliveryDate
                ? new Date(data.deliveryDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })
                : 'Heute (noch unbekannt)'}
            </span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {data.notes && (
        <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-300">Notizen</span>
          </div>
          <p className="text-sm text-slate-100">{data.notes}</p>
        </div>
      )}

      {/* Info note */}
      <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 mb-6">
        <p className="text-xs text-green-300">
          Nach der Bestätigung wird der Verkauf erfasst, der Kontakt als Kunde markiert
          {data.paymentType === 'leasing' ? ' und die Leasing-Nachverfolgung gestartet' : ''}.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex gap-3">
        <Button variant="ghost" onClick={onBack} className="flex-1" disabled={isSubmitting}>
          Zurück
        </Button>
        <Button onClick={onConfirm} className="flex-1" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Wird gespeichert...
            </>
          ) : (
            'Verkauf bestätigen'
          )}
        </Button>
      </div>
    </div>
  );
}
